import { COLORS } from '@btime/constants'
import { capitalizeIfLower, capitalizeSentenceIfLower } from '@btime/helpers'
import { compose, find, is, path, pathOr, prop, propEq, propOr } from 'ramda'
import React, { Fragment } from 'react'
import moment from 'moment'

import { usePdfOptionsFormActions } from '../../../../components/pdf/options/_form/PdfOptionsFormHandler'
import Markdown from '../../../../components/presentation/Markdown'
import LabelValue from '../../../../components/pdf/LabelValue'
import Separator from '../../../../components/pdf/Separator'
import Grid, { Col } from '../../../../components/pdf/Grid'
import Gallery from '../../../../components/pdf/Gallery'
import Image from '../../../../components/pdf/Image'
import Line from '../../../../components/pdf/Line'
import Text from '../../../../components/pdf/Text'
import View from '../../../../components/pdf/View'
import Tag from '../../../../components/pdf/Tag'

export const getValueFromOptions = (value, options) =>
  compose(capitalizeIfLower, propOr('__resposta_não_encontrada__', 'name'), find(propEq('id', value)))(options)

// TODO: Break component and reorganize folder
const getValue = item => {
  const type = compose(path(['question', 'type', 'name']))(item)
  const questionData = compose(path(['question', 'data']))(item) || {}
  const value = prop(['value'], item)

  if (value === undefined || !type) return { value }

  if (value === null && type !== 'action_plan') {
    return {
      value: <Tag label="N/A" color={COLORS.dark3} />,
      plainValue: true
    }
  }

  switch (type) {
    case 'compliance':
    case 'checkbox':
      var active = value == 1
      const { labels } = questionData
      return {
        value: (
          <Tag
            label={active ? labels?.true || 'Sim' : labels?.false || 'Não'}
            color={active ? COLORS.green : COLORS.red}
          />
        ),
        plainValue: true
      }

    case 'multiple':
      return { value: value.map(item => item.name).join(', ') }

    case 'pending':
      const pendencyName = compose(capitalizeIfLower, path(['pending', 'name']))(value)
      const pendencyDescription = compose(capitalizeSentenceIfLower, prop('description'))(value)

      return {
        value: <Tag label={pendencyName} color={COLORS.purple} />,
        plainValue: true,
        children: <Text>{pendencyDescription}</Text>
      }

    case 'datetime':
    case 'date':
      return {
        value: moment(value).format('DD/MM/YYYY' + (type === 'datetime' ? ' HH:mm' : ''))
      }

    case 'select':
      return { value: <Tag label={value?.name} color={COLORS.dark3} />, plainValue: true }

    case 'system_status':
      var active = value == 1
      return {
        value: <Tag label={active ? 'Ativo' : 'Inativo'} color={active ? COLORS.green : COLORS.red} />,
        plainValue: true
      }

    case 'sign':
      const src = prop('src', value)
      const doc = value.documentValue ? `(${value.documentValue})` : ''
      return {
        value: `${value.name} ${doc}`,
        children: !!src && <Image src={src} style={{ height: 'auto', width: '55%' }} />
      }

    case 'gallery':
      const items = (value || []).map(item => ({ ...propOr({}, 'document', item), ...item }))
      const numberOfItems = items.length
      const itemNumberLabel = numberOfItems > 1 ? `${numberOfItems} itens` : `${numberOfItems} item`

      return {
        value: itemNumberLabel,
        children: (
          <Fragment>
            <Separator weight={4} />
            <Gallery items={items} />
          </Fragment>
        )
      }

    case 'block':
      const blocks = Array.isArray(value) ? value : []
      return {
        value: `${blocks.length} itens`,
        children: (
          <>
            <Separator weight={4} />
            {blocks.map((replies, index) => (
              <View key={index} align="stretch">
                <Grid align="stretch">
                  <Line vertical width={5} height="auto" />
                  <Col width={12} />
                  <Col flex={1}>
                    {replies.map(reply => (
                      <Reply key={reply.id} reply={reply} />
                    ))}
                  </Col>
                </Grid>
                <Separator weight={4} />
              </View>
            ))}
          </>
        )
      }

    case 'location':
      return {
        value: path(['search'], value)
      }

    case 'place_location':
    case 'place_person':
      const places = prop('places', item) || value || []
      return {
        value: places.map(place => `${place.name} (${place.resume})`).join(', ')
      }

    case 'place':
    case 'user':
    case 'asset':
      let label = ''
      if (is(Array, value)) {
        return { value: value.map(prop('name')).join(', ') }
      }
      return { value: value.name }

    case 'action_plan':
      return {
        value: value?.map(item => item.yearlyId).join(', ')
      }

    default:
      return { value: (value || '').toString() }
  }
}

const Reply = ({ reply }) => {
  const { hideImages, showQuestionInfo } = usePdfOptionsFormActions()

  const label = compose(capitalizeSentenceIfLower, path(['question', 'name']))(reply)
  const notes = prop('notes', reply)

  const images = hideImages ? false : prop('documents', reply)

  const questionDocuments = pathOr([], ['question', 'documents'], reply)
  const questionDescription = path(['question', 'description'], reply)

  const { children, ...props } = getValue(reply)

  return (
    <View noWrap>
      <LabelValue label={label} {...props}>
        {!!showQuestionInfo && (
          <>
            {!!questionDescription && <Markdown>{questionDescription}</Markdown>}
            {!!questionDocuments.length && <Gallery items={questionDocuments} />}
          </>
        )}

        {children}
        {!!notes && (
          <div>
            <Separator weight={4} />
            <Text size="xxsmall" color={COLORS.dark4}>
              Anotações
            </Text>
            <Separator weight={1} />
            <Text>{notes}</Text>
          </div>
        )}
        {!!images && !!images.length && (
          <div>
            <Separator weight={4} />
            <Text size="xxsmall" bold color={COLORS.dark4}>
              Fotos
            </Text>
            <Separator weight={1} />
            <Gallery items={images} />
          </div>
        )}
      </LabelValue>
    </View>
  )
}

export default Reply
